import { supabase } from '@/lib/supabase'
import type { DossierContext, Task, TaskPriority, TasksOverview } from '@/features/preinvest/types'

/**
 * Local mutation helpers for the tasks API (spec-07 task E).
 *
 * The shared `@/lib/api` helper only exposes `apiGet`; writes need their own
 * wrapper. This mirrors that file's Supabase-bearer pattern so reads and writes
 * authenticate the same way, and lives in the tasks feature so it stays
 * mockable in tests (`vi.mock('@/features/preinvest/tasks/api')`).
 */

async function authHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }
  return headers
}

/**
 * Assignee scope for {@link listTasks}.
 * - `me` — tasks assigned to the caller ("Mijn taken").
 * - `unassigned` — tasks nobody owns yet (the "Team taken" backstop so
 *   board-created tasks never stay hidden).
 * - `all` — every team task, assigned or not.
 * - a raw user id — tasks assigned to that user.
 */
export type TaskAssigneeScope = 'me' | 'unassigned' | 'all' | (string & {})

/**
 * GET /api/v1/preinvest/tasks — list tasks, optionally scoped to one deal,
 * an assignee scope, and filtered by status. Backs the deal-preview's
 * interactive open-tasks list and the "Mijn/Team taken" views.
 *
 * `assignee` is optional and backward-compatible: omit it for the previous
 * behaviour (no assignee filter).
 */
export async function listTasks(params: {
  deal_id?: string
  status?: 'open' | 'done' | 'all'
  assignee?: TaskAssigneeScope
  include_archived?: boolean
}): Promise<Task[]> {
  const query = new URLSearchParams()
  if (params.deal_id) query.set('deal_id', params.deal_id)
  if (params.status) query.set('status', params.status)
  if (params.assignee) query.set('assignee', params.assignee)
  if (params.include_archived) query.set('include_archived', 'true')
  const response = await fetch(`/api/v1/preinvest/tasks?${query.toString()}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Taken laden mislukt (${response.status})`)
  }
  return (await response.json()) as Task[]
}

export interface CreateTaskInput {
  title: string
  priority: TaskPriority
  due_date: string | null
  /** Full assignee set, primary owner first (spec-21 D2); `[]` = unassigned. */
  assignee_ids: string[]
  /** Bind the task to a deal (dossier tasks). */
  deal_id?: string | null
  /** Bind the task to a startup (dossier tasks). */
  startup_id?: string | null
  /**
   * Dossier context (spec-44): omit for the server default (`preinvest`);
   * `relaties` marks a CRM follow-up task linked via `person_id`.
   */
  context?: DossierContext | 'relaties'
  /** Bind the task to a CRM contact (spec-44, `context: 'relaties'`). */
  person_id?: string | null
}

export type UpdateTaskInput = Partial<Pick<Task, 'status' | 'title' | 'priority' | 'due_date'>> & {
  /** Replaces the whole assignee set (spec-21 D2); `[]` clears it. */
  assignee_ids?: string[]
  /** Re-link (or clear) the CRM contact (spec-44). */
  person_id?: string | null
}

/**
 * GET /api/v1/preinvest/tasks/overview — the unified platform+portal task list
 * (spec-21 D6). `scope=all` is operator-only (403 otherwise); a portal outage
 * degrades to `portal_status !== 'ok'` with platform tasks still served.
 * `scope=unassigned` (spec-64) lists the tasks nobody owns, for every team member.
 * `archived=true` (spec-23d D4) is the operator archive view: requires
 * `scope=all`, returns only archived platform tasks, skips the portal merge.
 */
export async function getTasksOverview(params: {
  scope?: 'me' | 'all' | 'unassigned'
  status?: 'open' | 'done' | 'all'
  archived?: boolean
}): Promise<TasksOverview> {
  const query = new URLSearchParams()
  if (params.scope) query.set('scope', params.scope)
  if (params.status) query.set('status', params.status)
  if (params.archived) query.set('archived', 'true')
  const response = await fetch(`/api/v1/preinvest/tasks/overview?${query.toString()}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Taken laden mislukt (${response.status})`)
  }
  return (await response.json()) as TasksOverview
}

export async function createTask(input: CreateTaskInput): Promise<Task> {
  const response = await fetch('/api/v1/preinvest/tasks', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(input),
  })
  if (!response.ok) {
    throw new Error(`Verzoek mislukt (${response.status})`)
  }
  return (await response.json()) as Task
}

export async function updateTask(id: string, patch: UpdateTaskInput): Promise<Task> {
  const response = await fetch(`/api/v1/preinvest/tasks/${id}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(patch),
  })
  if (!response.ok) {
    throw new Error(`Verzoek mislukt (${response.status})`)
  }
  return (await response.json()) as Task
}

export async function deleteTask(id: string): Promise<void> {
  const response = await fetch(`/api/v1/preinvest/tasks/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  })
  // 204 No Content — nothing to parse.
  if (!response.ok) {
    throw new Error(`Verzoek mislukt (${response.status})`)
  }
}

export async function archiveTask(id: string): Promise<Task> {
  const response = await fetch(`/api/v1/preinvest/tasks/${id}/archive`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Verzoek mislukt (${response.status})`)
  }
  return (await response.json()) as Task
}

export async function unarchiveTask(id: string): Promise<Task> {
  const response = await fetch(`/api/v1/preinvest/tasks/${id}/unarchive`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Verzoek mislukt (${response.status})`)
  }
  return (await response.json()) as Task
}

/**
 * POST /api/v1/preinvest/tasks/archive-done — bulk archive of done platform
 * tasks (spec-23d D5). Without `dealId` every done task (operator-only); with
 * `dealId` only that deal's, for any team member (spec-65 U-008, the dossier's
 * "Archiveer alle afgeronde"). Returns the archived count.
 */
export async function archiveDoneTasks(
  options: { dealId?: string } = {},
): Promise<{ archived: number }> {
  const query = options.dealId ? `?deal_id=${encodeURIComponent(options.dealId)}` : ''
  const response = await fetch(`/api/v1/preinvest/tasks/archive-done${query}`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Verzoek mislukt (${response.status})`)
  }
  return (await response.json()) as { archived: number }
}
